import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import posthog from 'posthog-js';
import { ASSET_BASE_URL, API_BASE_URL } from '../config/constants';

// Helper to create a light tint from a hex color
const hexToLightTint = (hex, opacity = 0.08) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  // Mix with white based on opacity
  const mixedR = Math.round(r * opacity + 255 * (1 - opacity));
  const mixedG = Math.round(g * opacity + 255 * (1 - opacity));
  const mixedB = Math.round(b * opacity + 255 * (1 - opacity));
  return `rgb(${mixedR}, ${mixedG}, ${mixedB})`;
};

// Alternative hero copy ideas:
// "Stop drowning in Claude Code updates - ride the wave"

function Hero({
  tagline = "For engineers using Claude Code",
  headline = "See your Claude Code setup from above.",
  subtext = "Snap together Claude components like LEGOs to connect, and apply configurations directly to your repo. ",
  accentColor = "#c4704b",
  variant = "default"
}) {
  const accentTint = hexToLightTint(accentColor);
  const [copied, setCopied] = useState(false);
  const [devServerActive, setDevServerActive] = useState(false);
  const navigate = useNavigate();

  // Track page variant in GA4
  useEffect(() => {
    if (window.gtag) {
      window.gtag('event', 'page_variant', { variant });
    }
    posthog.capture('landing_viewed', { variant });
  }, [variant]);

  // Check if dev server is running
  useEffect(() => {
    const checkDevServer = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/`, {
          method: 'GET',
          mode: 'cors'
        });
        setDevServerActive(response.ok);
      } catch (error) {
        setDevServerActive(false);
      }
    };

    checkDevServer();
    const interval = setInterval(checkDevServer, 3000);
    return () => clearInterval(interval);
  }, []);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText('npx bonzai-tree -v');
      setCopied(true);
      posthog.capture('command_copied', { command: 'npx bonzai-tree -v' });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div style={{
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif'
    }}>
      <style>{`
        @media (max-width: 900px) {
          .hero-two-column {
            flex-direction: column !important;
            gap: 40px !important;
          }
          .hero-left-column {
            max-width: 100% !important;
            text-align: center !important;
          }
          .hero-headline {
            font-size: 36px !important;
          }
          .hero-subtext {
            font-size: 18px !important;
          }
          .hero-cta-text {
            font-size: 18px !important;
          }
          .hero-command-box {
            padding: 18px 24px !important;
            font-size: 16px !important;
          }
          .hero-right-column {
            max-width: 100% !important;
            width: 100% !important;
          }
          .hero-section {
            padding: 20px !important;
            height: auto !important;
            min-height: 100vh !important;
            padding-top: 100px !important;
          }
          .hero-email-link {
            justify-content: center !important;
          }
          .hero-features {
            padding: 60px 20px !important;
          }
        }
        @media (max-width: 500px) {
          .hero-headline {
            font-size: 28px !important;
          }
          .hero-command-pre {
            font-size: 14px !important;
          }
          .hero-features {
            padding: 40px 16px !important;
          }
        }
      `}</style>
      {/* Hero Section - 90vh */}
      <div className="hero-section" style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '90vh',
        width: '100%',
        padding: '40px',
        boxSizing: 'border-box',
        position: 'relative',
        zIndex: 1
      }}>
      {/* Header with logo and visualize button */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 24px',
        background: 'rgba(255, 255, 255, 0.75)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        zIndex: 100
      }}>
        {/* Logo */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <img
            src={`${ASSET_BASE_URL}/favicon.jpg`}
            alt="Bonzai"
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '6px'
            }}
          />
          <span style={{
            fontSize: '20px',
            fontWeight: '600',
            color: '#333'
          }}>
            bonzai
          </span>
        </div>

        {/* Navigation */}
        <nav style={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
          <span
            onClick={() => navigate('/canvas')}
            style={{
              fontSize: '16px',
              fontWeight: '500',
              color: '#333',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = '#666';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = '#333';
            }}
          >
            Workflow
          </span>
          <span
            onClick={() => navigate('/blog')}
            style={{
              fontSize: '16px',
              fontWeight: '500',
              color: '#333',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = '#666';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = '#333';
            }}
          >
            Blog
          </span>
        </nav>
      </div>

      {/* Two-column layout */}
      <div className="hero-two-column" style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        maxWidth: '1200px',
        width: '100%',
        gap: '60px'
      }}>
        {/* Left column - Text content */}
        <div className="hero-left-column" style={{
          flex: '1',
          maxWidth: '550px',
          textAlign: 'left'
        }}>
          {/* Made for CC users badge */}
          <p style={{
            fontSize: '14px',
            fontWeight: '600',
            margin: '0 0 16px 0',
            color: accentColor,
            letterSpacing: '0.5px',
            textTransform: 'uppercase'
          }}>
            {tagline}
          </p>

          {/* Main headline */}
          <h1 className="hero-headline" style={{
            fontSize: '56px',
            fontWeight: '700',
            margin: '0 0 32px 0',
            color: '#333',
            lineHeight: '1.2'
          }}>
            {headline}
          </h1>

          <p className="hero-subtext" style={{
            fontSize: '20px',
            fontWeight: '400',
            margin: '0 0 24px 0',
            color: '#555',
            lineHeight: '1.6'
          }}>
            {subtext}
          </p>


          {/* Command button and email section */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <button
                onClick={copyToClipboard}
                className="hero-command-box"
                style={{
                  position: 'relative',
                  margin: '0',
                  background: accentColor,
                  padding: '20px 32px',
                  borderRadius: '16px',
                  fontSize: '18px',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                  fontWeight: '500',
                  border: 'none',
                  color: '#fff',
                  cursor: 'pointer',
                  textAlign: 'center',
                  transition: 'all 0.2s ease',
                  display: 'inline-block',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                  letterSpacing: '0.02em'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.filter = 'brightness(1.1)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
                  const tooltip = e.currentTarget.nextElementSibling;
                  if (tooltip) {
                    tooltip.style.opacity = '1';
                    tooltip.style.visibility = 'visible';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.filter = 'brightness(1)';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                  const tooltip = e.currentTarget.nextElementSibling;
                  if (tooltip) {
                    tooltip.style.opacity = '0';
                    tooltip.style.visibility = 'hidden';
                  }
                }}
                onMouseDown={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.1)';
                }}
                onMouseUp={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
                }}
                aria-label="Copy command to clipboard"
              >
                $ npx bonzai-tree -v
              </button>
              <div style={{
                position: 'absolute',
                right: '24px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: '#fff',
                color: '#333',
                padding: '6px 12px',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '500',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                opacity: '0',
                visibility: 'hidden',
                transition: 'opacity 0.2s ease, visibility 0.2s ease',
                pointerEvents: 'none',
                whiteSpace: 'nowrap'
              }}>
                Click to copy
              </div>
            </div>

            <a
              href="mailto:sanjog@bonzai.dev"
              className="hero-email-link"
              style={{
                fontSize: '14px',
                color: '#333',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              Questions?{' '}
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: accentColor, fontWeight: '600' }}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" style={{ width: '16px', height: '16px' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
                </svg>
                Email us
              </span>
            </a>
          </div>
        </div>

        {/* Right column - Demo video */}
        <div className="hero-right-column" style={{
          flex: '1',
          maxWidth: '550px',
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
        }}>
          <video
            autoPlay
            loop
            muted
            playsInline
            style={{
              width: '100%',
              display: 'block'
            }}
          >
            <source src={`${ASSET_BASE_URL}/demo2.mov`} type="video/quicktime" />
            <source src={`${ASSET_BASE_URL}/demo2.mov`} type="video/mp4" />
          </video>
        </div>
      </div>
      </div>

      {/* Features Section */}
      <div className="hero-features" style={{
        background: accentTint,
        padding: '80px 40px'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '24px',
          maxWidth: '1200px',
          margin: '0 auto',
          flexWrap: 'wrap'
        }}>
          {/* Secure */}
          <div style={{
            background: '#fff',
            borderRadius: '16px',
            padding: '32px',
            flex: '1',
            minWidth: '280px',
            maxWidth: '360px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
          }}>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke={accentColor} style={{ width: '32px', height: '32px', marginBottom: '16px' }}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
            </svg>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#333',
              margin: '0 0 8px 0'
            }}>
              Secure
            </h3>
            <p style={{
              fontSize: '15px',
              color: '#666',
              lineHeight: '1.5',
              margin: 0
            }}>
              Runs entirely on localhost. Your code never leaves your machine.
            </p>
          </div>

          {/* Frictionless */}
          <div style={{
            background: '#fff',
            borderRadius: '16px',
            padding: '32px',
            flex: '1',
            minWidth: '280px',
            maxWidth: '360px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
          }}>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke={accentColor} style={{ width: '32px', height: '32px', marginBottom: '16px' }}>
              <path strokeLinecap="round" strokeLinejoin="round" d="m3.75 13.5 10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75Z" />
            </svg>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#333',
              margin: '0 0 8px 0'
            }}>
              Frictionless
            </h3>
            <p style={{
              fontSize: '15px',
              color: '#666',
              lineHeight: '1.5',
              margin: 0
            }}>
              No install or permissions. Just one npx command.
            </p>
          </div>

          {/* All in One Place */}
          <div style={{
            background: '#fff',
            borderRadius: '16px',
            padding: '32px',
            flex: '1',
            minWidth: '280px',
            maxWidth: '360px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
          }}>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke={accentColor} style={{ width: '32px', height: '32px', marginBottom: '16px' }}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 7.5l3 2.25-3 2.25m4.5 0h3m-9 8.25h13.5A2.25 2.25 0 0021 18V6a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 6v12a2.25 2.25 0 002.25 2.25z" />
            </svg>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#333',
              margin: '0 0 8px 0'
            }}>
              All in One Place
            </h3>
            <p style={{
              fontSize: '15px',
              color: '#666',
              lineHeight: '1.5',
              margin: 0
            }}>
              Included dev server with drawer and terminal. Code fast without switching windows.
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer style={{
        padding: '40px 24px',
        borderTop: '1px solid #eee',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '16px',
        fontSize: '14px',
        color: '#666'
      }}>
        <p style={{
          margin: 0,
          fontSize: '15px',
          fontStyle: 'italic',
          color: '#888'
        }}>
          Bonzai is a visual interface to snap together Claude Code configurations.
        </p>
        <div style={{ display: 'flex', gap: '24px' }}>
          <a href="mailto:sanjog@bonzai.dev" style={{ color: '#666', textDecoration: 'none' }}>Contact</a>
          <span style={{ color: '#ddd' }}>|</span>
          <a href="/privacy" style={{ color: '#666', textDecoration: 'none' }}>Privacy Policy</a>
          <span style={{ color: '#ddd' }}>|</span>
          <a href="/terms" style={{ color: '#666', textDecoration: 'none' }}>Terms of Service</a>
        </div>
      </footer>

      {/* Copied toast */}
      {copied && (
        <div style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          background: '#333',
          color: '#fff',
          padding: '12px 20px',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '500',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" style={{ width: '16px', height: '16px', color: '#22c55e' }}>
            <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
          </svg>
          Copied! Run it in your terminal.
        </div>
      )}
    </div>
  );
}

export default Hero;

